'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'

import {
  bootstrapSession,
  exchangeDashboardSession,
  DashboardExchangeError,
  isRateLimitedExchangeError,
  logout,
} from '@/lib/auth'
import { logger } from '@/lib/logger'
import { getSupabaseClient } from '@/lib/supabase'
import { withoutTrailingSlash } from '@/lib/utils'
import { buildContinuePath } from '@/lib/redirects'
import { setRateLimitNotice } from '@/lib/rate-limit-notice'

/**
 * OAuth error codes returned by Supabase/providers when auth flow is cancelled or fails.
 * @see https://supabase.com/docs/guides/auth/social-login
 */
type OAuthErrorCode =
  | 'access_denied' // User cancelled OAuth consent
  | 'server_error' // Provider server error
  | 'temporarily_unavailable' // Provider temporarily unavailable
  | 'invalid_request' // Malformed OAuth request
  | 'unauthorized_client' // Client not authorized for this grant
  | 'unsupported_response_type'
  | 'invalid_scope'
  | 'login_required' // User needs to login first
  | 'consent_required' // User needs to consent first
  | 'interaction_required' // User interaction required

/**
 * Cleans OAuth-related error parameters from the URL without triggering navigation.
 */
function cleanOAuthErrorParams(): void {
  try {
    const url = new URL(window.location.href)
    const paramsToClean = ['error', 'error_description', 'error_code', 'error_uri']
    let hasChanges = false

    for (const param of paramsToClean) {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param)
        hasChanges = true
      }
    }

    if (hasChanges) {
      window.history.replaceState({}, document.title, url.toString())
    }
  } catch (error) {
    logger.warn('[Auth] No fue posible limpiar los parámetros de error OAuth de la URL', { error })
  }
}

/**
 * Checks if the current URL contains OAuth error parameters from a failed/cancelled auth flow.
 * This happens when:
 * - User clicks "Cancel" or closes the Google consent window
 * - OAuth provider returns an error
 * - Network issues during OAuth redirect
 */
function checkForOAuthError(): {
  hasError: boolean
  code: string | null
  description: string | null
} {
  try {
    const params = new URLSearchParams(window.location.search)
    const error = params.get('error') as OAuthErrorCode | null
    const errorDescription = params.get('error_description')

    // Also check the hash fragment (some OAuth flows use fragment-based responses)
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const hashError = hashParams.get('error') as OAuthErrorCode | null
    const hashErrorDescription = hashParams.get('error_description')

    const finalError = error || hashError
    const finalDescription = errorDescription || hashErrorDescription

    return {
      hasError: Boolean(finalError),
      code: finalError,
      description: finalDescription,
    }
  } catch {
    return { hasError: false, code: null, description: null }
  }
}

export function LandingRedirector() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const searchKey = useMemo(() => searchParams?.toString() ?? '', [searchParams])
  const lastSearchKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (lastSearchKeyRef.current === searchKey) return
    lastSearchKeyRef.current = searchKey
    let cancelled = false

    void (async () => {
      const reason =
        searchParams?.get('reason') ??
        (typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('reason')
          : null)

      // Check for OAuth errors FIRST, before attempting any session operations
      const oauthError = checkForOAuthError()
      if (oauthError.hasError) {
        // Clean the URL to remove error params
        cleanOAuthErrorParams()

        // Log the error for debugging (non-production only for user-cancelled flows)
        if (oauthError.code === 'access_denied') {
          // User cancelled - this is expected behavior, just log at debug level
          if (process.env.NODE_ENV !== 'production') {
            logger.debug('[Auth] Usuario canceló el inicio de sesión con OAuth', {
              description: oauthError.description,
            })
          }
        } else {
          // Actual error - log at warning level
          logger.warn('[Auth] Error en el flujo OAuth', {
            code: oauthError.code,
            description: oauthError.description,
          })
        }

        // Don't attempt session bootstrap - let user try again manually
        return
      }

      // If the user was redirected here because they logged out, force sign out.
      if (reason === 'logout') {
        const supabase = getSupabaseClient()
        await supabase.auth.signOut()
        // Optional: Clean the URL
        const newUrl = new URL(window.location.href)
        newUrl.searchParams.delete('reason')
        window.history.replaceState({}, '', newUrl.toString())
        return // Stop execution, do not bootstrap
      }

      try {
        const session = await bootstrapSession()
        if (!session || cancelled) return

        let exchangeResult
        try {
          exchangeResult = await exchangeDashboardSession(session)
        } catch (error) {
          if (isRateLimitedExchangeError(error)) {
            const retryAfterMs = error.retryAfterMs ?? 30_000
            setRateLimitNotice({
              until: Date.now() + retryAfterMs,
              message: error.message,
            })
            if (process.env.NODE_ENV !== 'production') {
              logger.warn('[Auth] Intercambio SSO en cooldown (landing)', {
                code: error.code,
                retryAfterMs: error.retryAfterMs,
              })
            }
            return
          }
          await logout()
          if (process.env.NODE_ENV !== 'production') {
            if (error instanceof DashboardExchangeError) {
              logger.error('[Auth] Falló el intercambio SSO desde la landing', {
                code: error.code,
                error,
              })
            } else {
              logger.error('[Auth] Falló el intercambio SSO desde la landing', { error })
            }
          }
          return
        }

        if (!exchangeResult) return

        const path = buildContinuePath(searchParams.get('next'))
        const baseUrl = withoutTrailingSlash(process.env.NEXT_PUBLIC_APP_BASE_URL)

        if (baseUrl) {
          window.location.assign(`${baseUrl}${path}`)
        } else {
          router.replace(path)
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          logger.error('No fue posible redirigir tras bootstrap de sesión', { error })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [router, searchKey, searchParams])

  return null
}
